import React from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';

import { useCurrentActor } from 'components/contexts/CurrentActor';

import { personDisplayName } from 'components/helpers/users';

export default function RemoveModal({
  divisionName,
  firstName,
  id,
  isTrainingRegisterAllowed,
  lastName,
  onClose,
  onRemove,
  onRevoke,
  strategy,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.RemoveModal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const { data: domainUserDeletionWarningReport } = useQuery({
    queryKey: ['domainUserDeletionWarningReport', id, strategy],
    queryFn: async () => {
      const response = await axios.get(
        `/users/${id}/deletion_warning_reports`,
        { params: { strategy } },
      );
      return response.data;
    },
    enabled: true,
  });

  const isDeletion = strategy === 'delete';

  const assignedRiskAssessmentsCount =
    domainUserDeletionWarningReport?.data.attributes
      .assignedRiskAssessmentsCount || 0;

  const assignedScheduledContentReviewRequestsCount =
    domainUserDeletionWarningReport?.data.attributes
      .assignedScheduledContentReviewRequestsCount || 0;

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={onClose}
            size='md'
          >
            {i18nTShared('cancel')}
          </TextButton>
          <FilledButton color='red' onClick={isDeletion ? onRemove : onRevoke}>
            {i18nTShared('remove')}
          </FilledButton>
        </div>
      }
      isOpen={true}
      mode='letterbox'
      onClose={onClose}
      usingStandardDimensions={true}
    >
      <>
        <div className='modalbox-header tw-border-grey-100'>
          <h2 className='modalbox-header__title truncated-text-container tw-text-l tw-font-semibold tw-text-grey-900'>
            {isDeletion ?
              i18nTShared('deleteNameConfirmation', {
                interpolation: { escapeValue: false },
                name: personDisplayName({
                  attributes: { firstName, lastName },
                }),
              })
            : i18nT('title.revoke', {
                divisionName,
                firstName,
                interpolation: { escapeValue: false },
                lastName,
              })
            }
          </h2>
        </div>
        <div className='modalbox-body'>
          <div>
            <p>
              {isDeletion ?
                isTrainingRegisterAllowed ?
                  i18nT('disclaimer.deletion.trainingRegisterAllowed')
                : i18nT('disclaimer.deletion.default')
              : i18nT('disclaimer.revoke')}
            </p>
            {(assignedScheduledContentReviewRequestsCount > 0 ||
              assignedRiskAssessmentsCount > 0) && (
              <p>
                {i18nT(
                  `reviewDisclaimer.${isDeletion ? 'deletion' : 'revoke'}`,
                  {
                    interpolation: { escapeValue: false },
                    reviewCount: assignedScheduledContentReviewRequestsCount,
                    riskAssessmentCount: assignedRiskAssessmentsCount,
                    singularRAMSName:
                      currentActor.division.attributes.singularRAMSName,
                  },
                )}
              </p>
            )}
          </div>
        </div>
      </>
    </ModalBox>
  );
}

RemoveModal.propTypes = {
  divisionName: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  isTrainingRegisterAllowed: PropTypes.bool.isRequired,
  lastName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRevoke: PropTypes.func.isRequired,
  strategy: PropTypes.string.isRequired,
};
